/**
 * FederatedWrapper — wraps a federated component with:
 *   1. React ErrorBoundary (catches render errors)
 *   2. Suspense (shows loading placeholder)
 *   3. Auto-recovery when the Hub reconnects
 *
 * Usage:
 *   <FederatedWrapper fallback={<FallbackHeaderBar />}>
 *     <RemoteHeaderBar user={user} onLogout={logout} />
 *   </FederatedWrapper>
 */
import { Component, Suspense } from 'react';

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    console.error(
      '[Konitys Federation] Render error in federated component:',
      error,
      info.componentStack
    );
  }

  /**
   * Allow parent to reset the error state (e.g. when Hub reconnects).
   */
  reset() {
    this.setState({ hasError: false, error: null });
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback || null;
    }
    return this.props.children;
  }
}

export default function FederatedWrapper({
  children,
  fallback,
  loadingPlaceholder,
}) {
  // Default loading placeholder: invisible spacer that won't cause layout shift
  const loading = loadingPlaceholder || fallback || null;

  return (
    <ErrorBoundary fallback={fallback}>
      <Suspense fallback={loading}>
        {children}
      </Suspense>
    </ErrorBoundary>
  );
}
